/**
 * OBB çarpışması. ContactApi yalnız OBB bilir.
 * Katman kendi engel listesini `resolveContactLayer` ile çözer.
 * `candidatePosition`: çarpışma düzeltmesi hiç uygulanmamış dünya konumu.
 * Canlı model yazılmaz.
 */

import { Matrix4, Quaternion, Vector3 } from "three";
import { OBB } from "three/examples/jsm/math/OBB.js";
import { isPlaneCollider } from "../../../loader/colliderType";
import { SugarModel } from "../../../loader/SugarModel";
import { transformObb } from "../../../util/GeometryUtil";
import { Line } from "../../../shape/Line";
import { Room } from "../../../shape/Scene";
import {
  queryContacts,
  selectShortestDragPush,
  type CornerEdgeContact,
} from "./ContactApi";
import { cornerLineSnap, CORNER_SNAP_PROBE_M, inflateObbXZ } from "./CornerLineSnap";
import { isCornerLineSnapEnabled } from "../../../util/DebugFlags";
import {
  OBJECT_COLLISION_HITS,
  type AppliedPushDebug,
  type CornerLineSnapDebug,
  type ObjectCollisionHitsDetail,
  type NarrowHit,
  type Obstacle,
  type ObstacleKind,
  type SeparationDebug,
} from "./ObjectCollisionHits";
import type { FaceLatchMap } from "./ModelAligner";

export type { ObjectCollisionHitsDetail, NarrowHit, Obstacle, ObstacleKind };
export { OBJECT_COLLISION_HITS };

/** Her adım bir öteleme. L ve ikinci kenar için 4 adım. */
export const MAX_NARROW_RESOLVE_STEPS = 4;

/** Narrow çözüm çıktısı; model state’e burada dokunulmaz. */
export interface NarrowCollisionResolveResult {
  position: Vector3;
  /** Bu itme dönüş yazmaz; çağıran quaternion’ı korur. */
  rotation?: Quaternion;
  /** İlk aday pozisyondaki narrow isabetleri (çözüm döngüsüyle aynı hesap; tel/event tek kaynak). */
  hitsAtCandidate: NarrowHit[];
  /** İlk adaydaki temaslar. Karar bunları okur. */
  separations: SeparationDebug[];
  /** İlk uygulanan temas: köşeden kesişen kenara. Sıkışmada yoktur. */
  appliedPush?: AppliedPushDebug;
  /** İtmeden sonra köşe çizgisi hizası. */
  cornerSnaps: CornerLineSnapDebug[];
}

/**
 * Grab anında dondurulmuş yerel OBB + o anki matrixWorld.
 * Narrow / broad yalnızca burada ve `candidateWorldPosition` ile çalışır; canlı SugarModel OBB’si okunmaz.
 */
export interface CollisionWorkingSet {
  excludeModel: SugarModel;
  /** Kopya — sürüklerken world pozisyonu yalnızca aday vektörle kaydırılır. */
  grabWorldMatrix: Matrix4;
  broadLocal: OBB;
  compoundLocal: OBB[];
}

const _wmPosScratch = new Vector3();

function cloneObbWithDebugModel(obb: OBB, fallbackModel: SugarModel): OBB {
  const clone = obb.clone();
  const source = obb as any;
  const target = clone as any;
  target.model = source.model ?? fallbackModel;
  if (source.sourceModel) target.sourceModel = source.sourceModel;
  if (source.configurator) target.configurator = source.configurator;
  return clone;
}

export function createCollisionWorkingSet(
  model: SugarModel,
  grabWorldMatrix: Matrix4,
): CollisionWorkingSet {
  return {
    excludeModel: model,
    grabWorldMatrix: grabWorldMatrix.clone(),
    broadLocal: cloneObbWithDebugModel(model.getCollisionLocalObb(), model),
    compoundLocal: model
      .getCollisionLocalObbs()
      .map((o) => cloneObbWithDebugModel(o, model)),
  };
}

/** Çalışma kümesi + aday dünya konumu → geniş dünya OBB (mutasyon yok, yeni klon). */
export function worldSpaceBroadOBB(
  working: CollisionWorkingSet,
  candidateWorldPosition: Vector3,
): OBB {
  const transformed = transformObb(working.broadLocal, working.grabWorldMatrix);
  const obb = cloneObbWithDebugModel(transformed, working.excludeModel);
  obb.center.copy(transformed.center);
  obb.halfSize.copy(transformed.halfSize);
  obb.rotation.copy(transformed.rotation);
  _wmPosScratch.setFromMatrixPosition(working.grabWorldMatrix);
  obb.center.x += candidateWorldPosition.x - _wmPosScratch.x;
  obb.center.y += candidateWorldPosition.y - _wmPosScratch.y;
  obb.center.z += candidateWorldPosition.z - _wmPosScratch.z;
  return obb;
}

/** Compound self OBB’leri dünya uzayında (klon dizisi). */
export function worldSpaceCompoundOBBs(
  working: CollisionWorkingSet,
  candidateWorldPosition: Vector3,
): OBB[] {
  const wm = working.grabWorldMatrix;
  _wmPosScratch.setFromMatrixPosition(wm);
  const dx = candidateWorldPosition.x - _wmPosScratch.x;
  const dy = candidateWorldPosition.y - _wmPosScratch.y;
  const dz = candidateWorldPosition.z - _wmPosScratch.z;
  return working.compoundLocal.map((lo) => {
    const transformed = transformObb(lo, wm);
    const o = cloneObbWithDebugModel(transformed, working.excludeModel);
    o.center.copy(transformed.center);
    o.halfSize.copy(transformed.halfSize);
    o.rotation.copy(transformed.rotation);
    o.center.x += dx;
    o.center.y += dy;
    o.center.z += dz;
    return o;
  });
}

export function buildObstacles(
  scene: Room,
  selfBroad: OBB,
  selectedModel: SugarModel | undefined,
  ignoredSupport?: SugarModel,
): Obstacle[] {
  const out: Obstacle[] = [];

  const skipPeerModels =
    selectedModel != null && isPlaneCollider(selectedModel.data);
  scene.models?.forEach((child) => {
    if (!(child instanceof SugarModel) || child === selectedModel) return;
    if (ignoredSupport && child === ignoredSupport) return;
    if (skipPeerModels || isPlaneCollider(child.data)) return;
    pushModelObstacles(out, child, selfBroad);
  });

  return out;
}

/** Line duvarları. Model listesine girmez; duvar katmanı kendi çağırır. */
export function buildLineWallObstacles(scene: Room, selfBroad: OBB): Obstacle[] {
  const out: Obstacle[] = [];
  scene.childs?.forEach((child) => {
    if (!(child instanceof Line) || !child.obb) return;
    if (!selfBroad.intersectsOBB(child.obb)) return;
    (child.obb as any).line = child;
    out.push({
      kind: "wall",
      obb: child.obb,
      line: child,
      latchKey: `wall:${child.name}`,
    });
  });
  return out;
}

/** Model-only obstacles (no walls) — Configurator / peer product collision. */
export function buildPeerModelObstacles(
  peers: readonly SugarModel[],
  selfBroad: OBB,
  excludeModel: SugarModel | undefined,
): Obstacle[] {
  const out: Obstacle[] = [];
  const skipPeerModels =
    excludeModel != null && isPlaneCollider(excludeModel.data);
  for (const child of peers) {
    if (!(child instanceof SugarModel) || child === excludeModel) continue;
    if (skipPeerModels || isPlaneCollider(child.data)) continue;
    pushModelObstacles(out, child, selfBroad);
  }
  return out;
}

function pushModelObstacles(
  out: Obstacle[],
  model: SugarModel,
  selfBroad: OBB,
): void {
  const obbs = model.getCollisionWorldObbs();
  for (let part = 0; part < obbs.length; part++) {
    const ob = obbs[part];
    if (!selfBroad.intersectsOBB(ob)) continue;
    out.push({
      kind: "model",
      obb: ob,
      latchKey: `${model.uuid}:${part}`,
    });
  }
}

export function narrowPhaseIntersections(
  selfObbs: OBB[],
  obstacles: Obstacle[],
): NarrowHit[] {
  const hits: NarrowHit[] = [];
  for (const selfObb of selfObbs) {
    for (const obstacle of obstacles) {
      if (selfObb.intersectsOBB(obstacle.obb)) {
        hits.push({ selfObb, obstacle });
      }
    }
  }
  return hits;
}

const OPPOSE_DOT = -1e-4;

function collectContacts(hits: NarrowHit[]): CornerEdgeContact[] {
  const tokens = new Map<OBB, number>();
  const tokenOf = (self: OBB): number => {
    let token = tokens.get(self);
    if (token == null) {
      token = tokens.size;
      tokens.set(self, token);
    }
    return token;
  };
  const seen = new Set<string>();
  const contacts: CornerEdgeContact[] = [];
  for (const hit of hits) {
    const token = tokenOf(hit.selfObb);
    const pair = `${hit.obstacle.latchKey}@${token}`;
    if (seen.has(pair)) continue;
    seen.add(pair);
    contacts.push(
      ...queryContacts(
        hit.selfObb,
        hit.obstacle.obb,
        hit.obstacle.latchKey,
        String(token),
      ),
    );
  }
  return contacts;
}

/** Verilen OBB listesini çözer. Engel türüne bakmaz. */
export function resolveContactLayer(
  working: CollisionWorkingSet,
  candidatePosition: Vector3,
  getObstacles: (broad: OBB) => Obstacle[],
  options?: { cornerSnap?: boolean },
): NarrowCollisionResolveResult {
  const pos = candidatePosition.clone();
  let hitsAtCandidate: NarrowHit[] = [];
  let separations: SeparationDebug[] = [];
  let appliedPush: AppliedPushDebug | undefined;
  let prevPush: Vector3 | undefined;
  let cornerSnaps: CornerLineSnapDebug[] = [];
  const allowCornerSnap = options?.cornerSnap === true;

  for (let step = 0; step < MAX_NARROW_RESOLVE_STEPS; step++) {
    const broad = worldSpaceBroadOBB(working, pos);
    const obstacles = getObstacles(broad);
    const selfObbs = worldSpaceCompoundOBBs(working, pos);
    const hits = narrowPhaseIntersections(selfObbs, obstacles);
    const contacts = collectContacts(hits);
    const choice = selectShortestDragPush(contacts);
    if (step === 0) {
      hitsAtCandidate = hits.slice();
      separations = contacts.map((contact) => ({
        from: contact.corner.clone(),
        to: contact.edgeFoot.clone(),
        depth: contact.depth,
        chosen: contact === choice.chosen,
        cornerOwner: contact.cornerOwner,
        obstacleKey: contact.obstacleKey,
      }));
    }
    if (hits.length === 0 || !choice.chosen) break;
    if (prevPush && choice.translation.dot(prevPush) < OPPOSE_DOT) break;
    if (choice.translation.lengthSq() <= 1e-12) break;

    pos.x += choice.translation.x;
    pos.z += choice.translation.z;
    if (!appliedPush) {
      appliedPush = {
        from: choice.chosen.corner.clone(),
        to: choice.chosen.edgeFoot.clone(),
      };
    }
    prevPush = choice.translation;
  }

  if (allowCornerSnap && isCornerLineSnapEnabled()) {
    const snapBroad = inflateObbXZ(
      worldSpaceBroadOBB(working, pos),
      CORNER_SNAP_PROBE_M,
    );
    const snapObstacles = getObstacles(snapBroad);
    if (snapObstacles.length > 0) {
      const snap = cornerLineSnap(
        worldSpaceCompoundOBBs(working, pos),
        snapObstacles.map((obstacle) => obstacle.obb),
      );
      cornerSnaps = snap.snaps;
      pos.x += snap.translation.x;
      pos.z += snap.translation.z;
    }
  }

  return { position: pos, hitsAtCandidate, separations, appliedPush, cornerSnaps };
}

/**
 * `candidatePosition`: kullanıcı sürüklemesinin ham sonucu (narrow’ın çıkışı değil, girdisi).
 * Canlı model / OBB getter’larına dokunulmaz; bırakmada dönüşü çağırıcı modele yazar.
 */
export function computeNarrowCollisionResolution(
  scene: Room,
  working: CollisionWorkingSet,
  candidatePosition: Vector3,
  ignoredSupport: SugarModel | undefined,
  _latches: FaceLatchMap,
): NarrowCollisionResolveResult {
  return resolveContactLayer(
    working,
    candidatePosition,
    (broad) =>
      buildObstacles(scene, broad, working.excludeModel, ignoredSupport),
    { cornerSnap: true },
  );
}

/** Peer SugarModel OBB resolution — no Room / walls required. */
export function computePeerModelCollisionResolution(
  peers: readonly SugarModel[],
  working: CollisionWorkingSet,
  candidatePosition: Vector3,
  _latches: FaceLatchMap,
): NarrowCollisionResolveResult {
  return resolveContactLayer(
    working,
    candidatePosition,
    (broad) => buildPeerModelObstacles(peers, broad, working.excludeModel),
    { cornerSnap: true },
  );
}
